/* eslint-disable @typescript-eslint/no-explicit-any */
import { datadogRum } from '@datadog/browser-rum';
import { LogLevel } from '@grafana/faro-react';

import { SSU_SESSION_ID } from 'config/storage';
import { datadog } from 'services/datadog';
import { grafanaFaro } from 'services/grafanaFaro';

export interface RumService {
  init: (sessionId: string) => void;
  addError: (error: unknown, context?: object | undefined) => void;
  setUserProperty: (key: string, property: unknown) => void;
  addAction: (name: string, context?: object | undefined) => void;
  addWarning: (message: string, context?: object | undefined) => void;
  addRumGlobalContext: (key: string, property: string) => void;
  addFeatureFlagEvaluation: typeof datadogRum.addFeatureFlagEvaluation;
}

/**
 * Service for initializing and interacting with Datadog RUM (Real User Monitoring).
 */
export const rumService: RumService = {
  /**
   * Initializes the RUM service with a given session ID.
   * @param sessionId - The session ID to initialize the RUM service with.
   */
  init: (sessionId: string) => {
    sessionStorage.setItem(SSU_SESSION_ID, sessionId);

    if (!datadogRum.getInternalContext()?.session_id) {
      datadog.initRUM(sessionId);
    }
    grafanaFaro.initRUM(sessionId);
  },

  /**
   * Adds an error to the RUM service.
   * @param error - The error to be added.
   * @param context - Optional context information to be associated with the error.
   */
  addError: (
    error: unknown,
    context?: Record<PropertyKey, any> | undefined,
  ) => {
    datadogRum.addError(error, context);
    grafanaFaro.addError(error as Error, context);
  },

  /**
   * Sets a user property in the RUM service.
   * @param key - The key of the user property.
   * @param property - The value of the user property.
   */
  setUserProperty: (key: string, property: unknown) => {
    datadogRum.setUserProperty(key, property);
    grafanaFaro.setSessionProperty({ [key]: String(property) });
  },

  /**
   * Adds an action to the RUM service.
   * @param name - The name of the action.
   * @param context - Optional context information to be associated with the action.
   */
  addAction: (name: string, context?: Record<PropertyKey, any> | undefined) => {
    datadogRum.addAction(name, context);
    grafanaFaro.addAction(name, context);
  },

  /**
   * Adds a warning-like action to the RUM service without affecting error KPIs.
   * @param message - The message of the warning action.
   * @param context - Optional context information to be associated with the action.
   */
  addWarning: (
    message: string,
    context?: Record<PropertyKey, any> | undefined,
  ) => {
    const warningContext = {
      ...context,
      level: LogLevel.WARN,
    };

    grafanaFaro.addLog([message], {
      context,
      level: LogLevel.WARN,
    });

    datadogRum.addAction(message, warningContext);
  },

  addRumGlobalContext(key, value) {
    datadogRum.addRumGlobalContext(key, value);
    grafanaFaro.setSessionProperty({ [key]: String(value) });
  },

  /**
   * Adds a feature flag evaluation to the RUM service.
   * @param key - The feature flag key.
   * @param value - The evaluated value of the feature flag.
   */
  addFeatureFlagEvaluation(key, value) {
    datadogRum.addFeatureFlagEvaluation(key, value);
    // FIXME: Implement Grafana Faro feature flag tracking
  },
};
